import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import InAppBrowserWarning from '../../components/feature/InAppBrowserWarning';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import StepIndicator from './components/StepIndicator';
import CartStep from './components/CartStep';
import CheckoutStep from './components/CheckoutStep';
import OrderSummary from './components/OrderSummary';
import ConfirmStep from './components/ConfirmStep';

type Step = 'cart' | 'checkout' | 'confirm';
type PayMethod = 'tabby' | 'card' | 'apple_pay' | 'google_pay' | 'cod';

export interface OrderData {
  items: Array<{ id: string; name: string; price: number; quantity: number; image?: string }>;
  form: { name: string; phone: string; email: string; city: string; address: string };
  total: number;
  shipping: number;
  method: PayMethod;
  orderId: string;
  trackingNumber: string;
  timestamp: string;
}

export default function PaymentPage() {
  const { items, removeItem, updateQuantity, totalPrice, clearCart } = useCart();
  const { isLoggedIn, user } = useAuth();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<Step>('cart');
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    city: '',
    address: '',
  });

  const SHIPPING = totalPrice >= 300 ? 0 : 25;
  const total = totalPrice + SHIPPING;

  // Handle payment redirect callbacks
  useEffect(() => {
    if (searchParams.get('stripe_success') === 'true' || searchParams.get('tabby_success') === 'true') {
      const saved = localStorage.getItem('pending_order');
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as OrderData;
          setOrderData(parsed);
          localStorage.removeItem('pending_order');
        } catch {
          // ignore parse errors
        }
      }
      clearCart();
      setStep('confirm');
    } else if (searchParams.get('stripe_cancel') === 'true' || searchParams.get('tabby_cancel') === 'true') {
      setStep('checkout');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleConfirm = (method: PayMethod) => {
    const trackNum = `TR-${Date.now().toString(36).toUpperCase()}-${Math.floor(Math.random() * 9000 + 1000)}`;
    const data: OrderData = {
      items: items.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
      form,
      total,
      shipping: SHIPPING,
      method,
      orderId: `#${Math.floor(Math.random() * 90000 + 10000)}`,
      trackingNumber: trackNum,
      timestamp: new Date().toISOString(),
    };
    setOrderData(data);
    clearCart();
    setStep('confirm');
  };

  return (
    <main dir="rtl">
      <InAppBrowserWarning />
      <Navbar />

      {/* Page header */}
      <section className="pt-28 pb-8 px-6 lg:px-12 bg-[#FAFAF8] border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-[#C9A961] text-xl" />
            </div>
            <h1 className="text-3xl font-black text-gray-900">إتمام الشراء</h1>
          </div>
          <p className="text-gray-400 text-sm mr-11">خطوتان فقط لإتمام طلبك</p>
        </div>
      </section>

      <section className="py-10 px-6 lg:px-12 bg-[#F5F1E8] min-h-screen">
        <div className="max-w-5xl mx-auto">

          {step !== 'confirm' && <StepIndicator current={step} />}

          {step === 'confirm' ? (
            <ConfirmStep orderData={orderData} />
          ) : step === 'cart' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <CartStep
                  items={items}
                  onRemove={removeItem}
                  onUpdateQty={updateQuantity}
                  onNext={() => setStep('checkout')}
                />
              </div>
              <div className="lg:col-span-1">
                <OrderSummary
                  items={items}
                  totalPrice={totalPrice}
                  isLoggedIn={isLoggedIn}
                />
              </div>
            </div>
          ) : (
            <CheckoutStep
              form={form}
              total={total}
              shipping={SHIPPING}
              items={items}
              onChange={handleFormChange}
              onBack={() => setStep('cart')}
              onConfirm={handleConfirm}
            />
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
